import { type TileItem } from '#components/Tile/types.ts';
import { ImageSize, PosterSize, ProfileSize } from '#enums/imageSizes.ts';
import { SectionType } from '#enums/mediaTypes.ts';
import { buildImagePath } from '#helpers/buildImagePath.ts';
import { formatAge } from '#helpers/formatAge.ts';
import { formatAirDates } from '#helpers/formatAirDates.ts';
import { formatNumberOfSeasons } from '#helpers/formatNumberOfSeasons.ts';
import { formatReleaseDate } from '#helpers/formatReleaseDate.ts';
import { formatRuntime } from '#helpers/formatRuntime.ts';
import { type Config, type SearchListing } from '#types.ts';

export const formatSearchResults = (listings: SearchListing[] | null | undefined, { images }: Config) =>
  listings
    ? listings.reduce((acc: TileItem[], data) => {
        switch (data.__typename) {
          case 'Movie': {
            const metadata = [];

            if (data.releaseDate) {
              metadata.push(formatReleaseDate(data.releaseDate));
            }

            const runtime = formatRuntime(data.runtime);

            if (runtime) {
              metadata.push(runtime);
            }

            acc.push({
              description: data.overview,
              id: data.id,
              metadata,
              src: buildImagePath(data.posterPath, ImageSize.Poster, PosterSize.SM, images),
              status: data.movieStatus,
              tagline: data.tagline ?? undefined,
              title: data.title,
              type: SectionType.Movie,
              voteAverage: data.voteAverage,
            });

            break;
          }

          case 'Person': {
            const metadata = [];

            if (data.birthday) {
              metadata.push(formatAge(data.birthday, data.deathday));
            }

            if (data.placeOfBirth) {
              metadata.push(data.placeOfBirth);
            }

            acc.push({
              description: data.biography,
              id: data.id,
              metadata,
              src: buildImagePath(data.profilePath, ImageSize.Profile, ProfileSize.MD, images),
              subTitle: data.knownForDepartment ?? '',
              title: data.name,
              type: SectionType.Person,
            });

            break;
          }

          case 'Tv': {
            const metadata = [];

            if (data.firstAirDate) {
              metadata.push(formatAirDates(data.firstAirDate, data.lastAirDate, data.tvStatus));
            }

            if (data.numberOfSeasons) {
              metadata.push(formatNumberOfSeasons(data.numberOfSeasons));
            }

            acc.push({
              description: data.overview,
              id: data.id,
              metadata,
              src: buildImagePath(data.posterPath, ImageSize.Poster, PosterSize.SM, images),
              status: data.tvStatus,
              tagline: data.tagline ?? undefined,
              title: data.name,
              type: SectionType.Tv,
              voteAverage: data.voteAverage,
            });

            break;
          }

          // no default
        }

        return acc;
      }, [])
    : [];
